import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { loadPostcode, normalizePostcodeResult, type PostcodeResult } from "./address-search";

type AddressSearchModalProps = {
  open: boolean;
  onClose: () => void;
  onSelect: (result: PostcodeResult) => void;
};

export function AddressSearchModal({ open, onClose, onSelect }: AddressSearchModalProps) {
  const embedRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  const onSelectRef = useRef(onSelect);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  useEffect(() => { onSelectRef.current = onSelect; }, [onSelect]);

  useEffect(() => {
    if (!open || !embedRef.current) return;
    let cancelled = false;
    const host = embedRef.current;
    host.replaceChildren();
    setError("");
    setLoading(true);

    void loadPostcode()
      .then((daum) => {
        if (cancelled) return;
        const postcode = new daum.Postcode({
          oncomplete: (data) => {
            onSelectRef.current(normalizePostcodeResult(data));
            onCloseRef.current();
          },
          onresize: ({ height }) => {
            host.style.height = `${Math.max(400, height)}px`;
          },
        });
        postcode.embed(host, { autoClose: false });
        setLoading(false);
      })
      .catch((caught) => {
        if (cancelled) return;
        setLoading(false);
        setError(caught instanceof Error ? caught.message : "주소검색 서비스에 연결할 수 없습니다.");
      });

    return () => { cancelled = true; };
  }, [attempt, open]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open]);

  if (!open) return null;

  return (
    <div className="address-search-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="address-search-modal" role="dialog" aria-modal="true" aria-labelledby="address-search-title">
        <header>
          <h2 id="address-search-title">주소 검색</h2>
          <button type="button" aria-label="주소 검색 닫기" onClick={onClose}><X aria-hidden="true" size={22} /></button>
        </header>
        <div className="address-search-frame">
          <div className="address-search-embed" ref={embedRef} />
          {loading ? <p className="address-search-status">주소검색을 불러오는 중입니다.</p> : null}
          {error ? <div className="address-search-status error" role="alert"><p>{error}</p><button type="button" onClick={() => setAttempt((value) => value + 1)}>다시 시도</button></div> : null}
        </div>
      </section>
    </div>
  );
}
